import React, { useState, useEffect, useContext } from 'react';
import { SettingsContext } from '../../context/SettingsProvider';
import { UserContext } from '../../context/UserProvider';
import { FiLock, FiUnlock } from 'react-icons/fi';
import { IoIosSend } from 'react-icons/io';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import PasswordStrengthIndicator from '../../components/display/PasswordStrengthIndicator';
import DeleteModal from '../../components/modals/DeleteModal';
import saveIcon from '../../assets/icons/save.svg';
import open from '../../assets/icons/eye-regular.svg';
import closed from '../../assets/icons/eye-slash-regular.svg';

export default function Accounts() {
  const {
    getStaffAccountData,
    createStaffAccount,
    accounts,
    deleteStaffAccount,
    updateStaffAccount,
    inviteUser,
  } = useContext(SettingsContext);
  const {
    darkMode,
    isExpanded,
    setUserPreference,
    hideInActive,
    hideInActiveUsers,
    setHideInActiveUsers,
  } = useContext(UserContext);

  const [users, setUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  // eslint-disable-next-line
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUser, setDeleteUser] = useState(null);
  const [isManualCreation, setIsManualCreation] = useState(true);

  const [newUser, setNewUser] = useState({
    email: '',
    username: '',
    password: '',
    role: 'Staff',
  });

  const openDelete = (id) => {
    setShowDeleteModal(true);
    setDeleteUser(id);
  };

  const closeDelete = () => {
    setShowDeleteModal(false);
    setDeleteUser(null);
  };

  const toggleCreateMode = () => {
    setIsManualCreation((prev) => !prev);
    setNewUser({
      email: '',
      username: '',
      password: '',
      role: 'Staff',
      locked: false,
      notifications: false,
    });
  };

  const handleDeleteUser = async () => {
    if (deleteUser !== null) {
      try {
        await deleteStaffAccount(deleteUser);
        const updatedUsers = users.filter((user) => user.id !== deleteUser);
        setUsers(updatedUsers);
        closeDelete();
        setShowSuccess(true);
        setText('User deleted successfully');
      } catch (error) {
        console.error('Error deleting user: ', error);
        setShowError(true);
        setText('Failed to delete user');
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({ ...prev, [name]: value }));
  };

  const startSpinner = () => {
    setShowSpinner(true);
    setTimeout(() => {
      setShowSpinner(false);
    }, 3000);
  };

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const handleInvite = async () => {
    if (!newUser.email) {
      alert('Please fill in the email field.');
      return;
    }
    startSpinner();
    try {
      const invitedUser = await inviteUser({ email: newUser.email });
      setUsers((prev) => [...prev, invitedUser]);
      setNewUser((prev) => ({
        ...prev,
        email: '',
      }));
      setShowSuccess(true);
      setText('User invited successfully');
    } catch (error) {
      console.error('Error inviting user: ', error);
      setShowError(true);
      setText('Failed to invite user');
    } finally {
      setShowSpinner(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.email) {
      alert('Please fill in username, email, and password fields.');
      return;
    }
    startSpinner();
    try {
      const createdUser = await createStaffAccount({
        email: newUser.email,
        username: newUser.username,
        password: newUser.password,
        role: newUser.role,
      });
      setUsers((prev) => [...prev, createdUser]);
      setNewUser((prev) => ({
        ...prev,
        email: '',
        username: '',
        password: '',
        role: 'Staff',
      }));
      setShowSuccess(true);
      setText('Account created successfully');
    } catch (error) {
      console.error('Error creating account: ', error);
      setShowError(true);
      setText('Failed to create account');
    } finally {
      setShowSpinner(false);
    }
  };

  const handleLockChange = async (id) => {
    const user = users.find((u) => u.id === id);
    if (!user) {
      console.error('User not found');
      return;
    }
    const updatePayload = { active: !user.active };
    try {
      await updateStaffAccount(id, updatePayload);
      const updatedUsers = users.map((u) =>
        u.id === id ? { ...u, active: updatePayload.active } : u
      );
      setUsers(updatedUsers);
      setShowSuccess(true);
      setText(`User ${updatePayload.active ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error updating user:', error);
      setShowError(true);
      setText(`Failed to ${updatePayload.active ? 'enable' : 'disable'} user`);
    }
  };

  const handleHideInActiveUsersToggle = async () => {
    const newHideInActiveUsers = !hideInActiveUsers;
    await setUserPreference({
      newDarkMode: darkMode,
      newIsExpanded: isExpanded,
      newHideInActive: hideInActive,
      newHideInActiveUsers: newHideInActiveUsers,
    });
    setHideInActiveUsers(newHideInActiveUsers);
  };

  useEffect(() => {
    getStaffAccountData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUsers(accounts);
  }, [accounts]);

  return (
    <div id="accounts" className="w-full px-4 py-6 sm:w-3/4 sm:px-12">
      {isManualCreation ? (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="email"
              className="block mb-2 dark:text-gray-200 select-none"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={newUser.email}
              maxLength="100"
              onChange={handleNewUserChange}
              autoComplete="new-email"
              required
              className="w-full sm:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
            />
          </div>
          <div>
            <label
              htmlFor="username"
              className="block mb-2 dark:text-gray-200 select-none"
            >
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={newUser.username}
              maxLength="50"
              onChange={handleNewUserChange}
              autoComplete="new-username"
              className="w-full sm:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block mb-2 dark:text-gray-200 select-none"
            >
              Password
              {!isPasswordValid && (
                <small className="text-red-500"> (Password is too short) </small>
              )}
            </label>
            <div className="relative w-full sm:w-1/2">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={newUser.password}
                maxLength="256"
                onChange={handleNewUserChange}
                autoComplete="new-password"
                className={`w-full px-3 py-2 border pr-10 ${
                  isPasswordValid ? 'border-gray-300' : 'border-red-500'
                } rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700`}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-2 flex items-center cursor-pointer"
              >
                <img
                  src={showPassword ? open : closed}
                  alt="Toggle"
                  className="h-6 w-6"
                />
              </button>
            </div>
            <div className="relative w-full my-3 sm:w-1/2">
            <PasswordStrengthIndicator password={newUser.password} />
            </div>
          </div>
          <div>
            <label
              htmlFor="role"
              className="block mb-2 dark:text-gray-200 select-none"
            >
              Role
            </label>
            <select
              id="role"
              name="role"
              value={newUser.role}
              onChange={handleNewUserChange}
              className="w-full sm:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-stone-700 dark:text-gray-300 dark:border-stone-700"
            >
              <option value="Staff">Staff</option>
              <option value="Admin">Admin</option>
            </select>
          </div>
          <div className="flex items-center gap-2 py-4">
            <button
              type="button"
              className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-600 dark:bg-indigo-500 dark:hover:bg-indigo-500 rounded-md focus:bg-blue-600"
              onClick={handleAddUser}
            >
              {showSpinner ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-300 inline-block mr-2.5" />
              ) : (
                <img
                  src={saveIcon}
                  alt="Save"
                  className="w-4 h-4 inline-block mr-1.5"
                />
              )}
              {showSpinner ? 'Saving' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={toggleCreateMode}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Switch to Invite
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="email"
              className="block mb-2 dark:text-gray-200 select-none"
            >
              Email
            </label>
            <div className="flex items-center w-full sm:w-1/2 border border-gray-300 rounded-md focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 dark:bg-stone-700 dark:border-stone-700">
              <input
                type="text"
                id="email"
                name="email"
                value={newUser.email}
                maxLength="50"
                onChange={handleNewUserChange}
                autoComplete="new-email"
                className="flex-grow px-3 py-2 rounded-md focus:outline-none dark:bg-stone-700 dark:text-gray-300"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="px-6 py-2 text-white bg-blue-600 hover:bg-blue-600 dark:bg-indigo-500 dark:hover:bg-indigo-500 rounded-md"
              onClick={handleInvite}
            >
              {showSpinner ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-300 inline-block mr-2.5" />
              ) : (
                <IoIosSend className="inline-block h-5 w-5 mr-1.5" />
              )}
              {showSpinner ? 'Sending...' : 'Send Invite'}
            </button>
            <button
              type="button"
              onClick={toggleCreateMode}
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              Switch to Create
            </button>
          </div>
        </div>
      )}
      <div className="mt-5 max-w-4xl rounded-lg bg-white p-6 shadow-lg dark:bg-stone-900">
        <div className="checkbox-container">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={hideInActiveUsers}
              onChange={handleHideInActiveUsersToggle}
            />
            <span className="text-base font-semibold text-gray-600 dark:text-gray-300">
              Hide Inactive Users
            </span>
          </label>
        </div>
        <div className="max-h-96 overflow-x-hidden">
          <div className="-mx-4 flex flex-wrap">
            {users &&
              users.length > 0 &&
              users
                .filter((user) => !hideInActiveUsers || user.active)
                .map((user) => (
                  <div
                    key={user.id}
                    className="w-full px-4 sm:w-1/2 md:w-1/3 lg:w-1/4"
                  >
                    <div className="my-4 max-w-sm overflow-hidden rounded-lg bg-white shadow-lg dark:bg-stone-800">
                      <div className="px-6 py-4">
                        <div className="mb-2 select-none text-xl font-bold dark:text-gray-200">
                          {user.username}
                        </div>
                        <p className="select-none text-base text-gray-700 dark:text-gray-400">
                          Role: {user.role}
                        </p>
                      </div>
                      <div className="px-6 pb-2 pt-4 flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleLockChange(user.id)}
                          className="rounded-lg px-1.5 py-2 text-white"
                        >
                          {user.active ? (
                            <FiUnlock className="inline-block h-5 w-5 text-blue-500 hover:text-blue-600 dark:text-indigo-500 dark:hover:text-indigo-600" />
                          ) : (
                            <FiLock className="inline-block h-5 w-5 text-gray-300 hover:text-gray-400 dark:text-indigo-300 dark:hover:text-indigo-400" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => openDelete(user.id)}
                          className="flex items-center rounded px-2 pt-[3px] text-red-400 hover:text-red-500 font-semibold"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="inline-block align-middle w-[22px] h-[22px]"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
          </div>
          {showDeleteModal && deleteUser && (
            <DeleteModal
              title="Are you sure you want to delete this user?"
              onCancel={closeDelete}
              onDelete={handleDeleteUser}
            />
          )}
          {showSuccess && (
            <SuccessNotification
              show={showSuccess}
              onClose={closeNotification}
              text={text}
            />
          )}
          {showError && (
            <ErrorNotification
              show={showError}
              onClose={closeNotification}
              text={text}
            />
          )}
        </div>
      </div>
    </div>
  );
}
